import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, KeyRound, CheckCircle, AlertTriangle } from 'lucide-react';
import loginBackground from '@/assets/login-background.png';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import useBrandSettings from '@/hooks/useBrandSettings';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: brandSettings } = useBrandSettings();

  // Verificar se há uma sessão de recovery válida
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Verificar se existe sessão (usuário clicou no link de recovery)
      if (session?.user) {
        setIsValidSession(true);
      } else {
        // Aguardar um pouco para o Supabase processar o token da URL
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (event === 'PASSWORD_RECOVERY' && session) {
            setIsValidSession(true);
          } else if (event === 'SIGNED_IN' && session) {
            setIsValidSession(true);
          }
        });
        
        // Timeout para mostrar erro se não houver sessão
        setTimeout(() => {
          if (isValidSession === null) {
            setIsValidSession(false);
          }
        }, 3000);
        
        return () => subscription.unsubscribe();
      }
    };
    
    checkSession();
  }, [isValidSession]);

  const validatePassword = (): boolean => {
    if (password.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'A senha deve ter pelo menos 6 caracteres.',
        variant: 'destructive',
      });
      return false;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: 'Senhas não conferem',
        description: 'A confirmação de senha deve ser igual à senha.',
        variant: 'destructive',
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });
      
      if (error) {
        console.error('Erro ao atualizar senha:', error);
        toast({
          title: 'Erro ao redefinir senha',
          description: error.message || 'Não foi possível atualizar sua senha.',
          variant: 'destructive',
        });
        return;
      }
      
      setIsSuccess(true);
      toast({
        title: 'Senha redefinida!',
        description: 'Sua senha foi atualizada com sucesso.',
      });
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        supabase.auth.signOut();
        navigate('/login');
      }, 3000);
      
    } catch (error: any) {
      console.error('Erro:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Estado de carregamento enquanto verifica sessão
  if (isValidSession === null) {
    return (
      <div 
        className="flex flex-col min-h-screen p-4 relative bg-cover bg-center bg-no-repeat items-center justify-center"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        <div className="relative z-10 text-center">
          <svg className="animate-spin h-8 w-8 text-white mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-white/80">Verificando link de recuperação...</p>
        </div>
      </div>
    );
  }

  // Link inválido ou expirado
  if (isValidSession === false) {
    return (
      <div 
        className="flex flex-col min-h-screen p-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md relative z-10 animate-fade-in">
            <Card className="border border-white/10 bg-black/30 backdrop-blur-xl shadow-2xl rounded-2xl">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-amber-500/20 rounded-full">
                    <AlertTriangle className="h-12 w-12 text-amber-500" />
                  </div>
                </div>
                <CardTitle className="text-white">Link Inválido ou Expirado</CardTitle>
                <CardDescription>
                  O link de recuperação não é mais válido
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-white/70 text-sm">
                  Links de recuperação expiram após 1 hora. Solicite um novo link.
                </p>
                <Link to="/esqueci-senha">
                  <Button className="w-full bg-imperial-red hover:bg-red-700">
                    Solicitar Novo Link
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-white/70 hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar para o login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex flex-col min-h-screen p-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      {/* Branding */}
      <div className="flex-1 flex items-end justify-center pb-8 relative z-10 animate-fade-in">
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img 
              src={brandSettings?.symbolLight || brandSettings?.symbolDark || 'https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/symbol_dark_1764955794247.png'} 
              alt={brandSettings?.platformName || 'AutoFluxo'} 
              className="h-16 drop-shadow-2xl"
            />
            <img 
              src="https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/logo_text_dark_1764955810339.png"
              alt={brandSettings?.platformName || 'AutoFluxo'} 
              className="h-12 drop-shadow-lg"
            />
          </div>
          <p className="text-white/80 drop-shadow-md">
            {brandSettings?.platformTagline || 'O sistema que faz sua revenda crescer'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-start justify-center pt-8">
        <div className="w-full max-w-md relative z-10 animate-fade-in">
          <Card className="border border-white/10 bg-black/30 backdrop-blur-xl shadow-2xl rounded-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-white">
                {isSuccess ? 'Senha Redefinida!' : 'Redefinir Senha'}
              </CardTitle>
              <CardDescription>
                {isSuccess 
                  ? 'Você será redirecionado para o login'
                  : 'Digite sua nova senha'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSuccess ? (
                <div className="text-center space-y-6">
                  <div className="flex justify-center">
                    <div className="p-4 bg-green-500/20 rounded-full">
                      <CheckCircle className="h-12 w-12 text-green-500" />
                    </div>
                  </div>
                  <p className="text-white/80 text-sm">
                    Sua senha foi atualizada com sucesso. Redirecionando...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <PasswordInput
                      placeholder="Nova senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-black/30 border-white/20 text-white placeholder:text-white/70 backdrop-blur-sm focus:bg-black/40 focus:border-white/40"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <PasswordInput
                      placeholder="Confirmar nova senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-black/30 border-white/20 text-white placeholder:text-white/70 backdrop-blur-sm focus:bg-black/40 focus:border-white/40"
                      required
                    />
                  </div>
                  <p className="text-white/50 text-xs">
                    A senha deve ter pelo menos 6 caracteres.
                  </p>
                  <Button 
                    type="submit" 
                    className="w-full bg-imperial-red hover:bg-red-700"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Salvando...
                      </span>
                    ) : (
                      <>
                        <KeyRound className="mr-2 h-4 w-4" />
                        Redefinir Senha
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Back to login */}
          {!isSuccess && (
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-white/70 hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar para o login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
